/**
 * Fixed-timestep accumulator. The render loop feeds it real frame time; it
 * reports how many fixed simulation ticks to run. Gameplay systems (from
 * Phase 3 onwards) advance only on ticks, so the simulation behaves the same
 * at 30 fps, 144 fps, or after a frame hitch.
 *
 * Phase 0 registers no gameplay systems; the clock only counts ticks so the
 * debug overlay can prove it runs independently of frame rate.
 */
export type TickHandler = (tick: number, dtSeconds: number) => void;

export class SimulationClock {
  readonly tickSeconds: number;
  private readonly maxTicksPerFrame: number;
  private accumulator = 0;
  private tickCount = 0;
  private paused = false;
  private readonly handlers: TickHandler[] = [];

  constructor(ticksPerSecond: number, maxTicksPerFrame: number) {
    if (ticksPerSecond <= 0) throw new Error('ticksPerSecond must be positive');
    this.tickSeconds = 1 / ticksPerSecond;
    this.maxTicksPerFrame = maxTicksPerFrame;
  }

  get ticks(): number {
    return this.tickCount;
  }

  /** Fraction (0..1) of the way to the next tick: lets the renderer interpolate between ticks. */
  get alpha(): number {
    return Math.min(1, this.accumulator / this.tickSeconds);
  }

  get isPaused(): boolean {
    return this.paused;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) this.accumulator = 0;
  }

  onTick(handler: TickHandler): () => void {
    this.handlers.push(handler);
    return () => {
      const i = this.handlers.indexOf(handler);
      if (i >= 0) this.handlers.splice(i, 1);
    };
  }

  /**
   * Advance by real elapsed seconds. Returns the number of ticks executed.
   * Excess time beyond maxTicksPerFrame is dropped (the game slows down
   * rather than freezing to catch up).
   */
  advance(elapsedSeconds: number): number {
    if (this.paused || !(elapsedSeconds > 0)) return 0;
    this.accumulator += elapsedSeconds;
    let ran = 0;
    while (this.accumulator >= this.tickSeconds && ran < this.maxTicksPerFrame) {
      this.accumulator -= this.tickSeconds;
      this.tickCount++;
      ran++;
      for (const h of this.handlers) h(this.tickCount, this.tickSeconds);
    }
    if (ran === this.maxTicksPerFrame && this.accumulator >= this.tickSeconds) {
      this.accumulator = 0;
    }
    return ran;
  }
}
